import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, User } from "lucide-react";
import CustomerLayout from "@/components/customer/CustomerLayout";
import { useAuth } from "@/context/AuthContext";
import apiClient, { formatApiErrorDetail } from "@/lib/api";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function CustomerProfilePage() {
  const { user, updateUser } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    avatar: user?.avatar || "",
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Nama wajib diisi.";
    if (!form.email.trim()) errs.email = "Email wajib diisi.";
    else if (!EMAIL_RE.test(form.email.trim())) errs.email = "Format email tidak valid.";
    if (form.phone && !/^[0-9+\-\s()]{6,20}$/.test(form.phone.trim()))
      errs.phone = "Format nomor telepon tidak valid.";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        avatar: form.avatar.trim() || null,
      };
      const { data } = await apiClient.put("/customer/profile", payload);
      updateUser(data);
      setForm({
        name: data.name || "",
        email: data.email || "",
        phone: data.phone || "",
        avatar: data.avatar || "",
      });
      toast.success("Profil berhasil diperbarui.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const initials = (form.name || "?").trim().charAt(0).toUpperCase();

  return (
    <CustomerLayout testid="customer-profile-page">
      <h1 className="font-heading text-3xl font-bold tracking-tight">Profil Saya</h1>
      <p className="mt-2 text-sm text-muted-foreground">Perbarui informasi akunmu.</p>

      <div className="mt-6 grid lg:grid-cols-3 gap-6">
        {/* Avatar preview */}
        <div className="lg:col-span-1">
          <div className="bg-card border border-border rounded-2xl p-6 text-center" data-testid="customer-profile-preview">
            <div className="mx-auto w-24 h-24 rounded-full overflow-hidden bg-secondary grid place-items-center">
              {form.avatar ? (
                <img src={form.avatar} alt={form.name} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
              ) : (
                <span className="font-heading text-3xl font-bold text-primary">{initials}</span>
              )}
            </div>
            <p className="mt-4 font-heading text-lg font-semibold truncate" data-testid="customer-profile-preview-name">{form.name || "-"}</p>
            <p className="text-sm text-muted-foreground truncate">{form.email || "-"}</p>
            <span className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-primary bg-secondary px-3 py-1 rounded-full">
              <User size={12} /> Customer
            </span>
          </div>
        </div>

        {/* Edit form */}
        <form onSubmit={onSubmit} noValidate className="lg:col-span-2 bg-card border border-border rounded-2xl p-6 space-y-5" data-testid="customer-profile-form">
          <div>
            <label className="block text-sm font-medium mb-1.5">Nama</label>
            <input
              type="text"
              value={form.name}
              onChange={set("name")}
              data-testid="customer-profile-name-input"
              className="w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30"
              placeholder="Nama lengkap"
            />
            {errors.name && <p className="mt-1 text-xs text-destructive" data-testid="customer-profile-name-error">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1.5">Email</label>
            <input
              type="email"
              value={form.email}
              onChange={set("email")}
              data-testid="customer-profile-email-input"
              className="w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30"
              placeholder="email@contoh.com"
            />
            {errors.email && <p className="mt-1 text-xs text-destructive" data-testid="customer-profile-email-error">{errors.email}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1.5">No. Telepon</label>
            <input
              type="tel"
              value={form.phone}
              onChange={set("phone")}
              data-testid="customer-profile-phone-input"
              className="w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30"
              placeholder="0812xxxxxxx"
            />
            {errors.phone && <p className="mt-1 text-xs text-destructive" data-testid="customer-profile-phone-error">{errors.phone}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1.5">URL Avatar <span className="text-muted-foreground font-normal">(opsional)</span></label>
            <input
              type="url"
              value={form.avatar}
              onChange={set("avatar")}
              data-testid="customer-profile-avatar-input"
              className="w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30"
              placeholder="https://..."
            />
          </div>

          <div className="pt-2">
            <button
              type="submit"
              disabled={saving}
              data-testid="customer-profile-save-button"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-semibold bg-primary text-primary-foreground hover:scale-105 active:scale-95 transition-transform disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              {saving ? "Menyimpan..." : "Simpan Perubahan"}
            </button>
          </div>
        </form>
      </div>
    </CustomerLayout>
  );
}
